/**
 * SalesChart Utility Functions
 */
import { TIME_PERIODS, PRICE_BANDS, MIN_DOM_SAMPLE, MIN_DOM_COVERAGE } from './types.js';
/**
 * True if a price falls inside the selected PRICE_BANDS entry. Shared by
 * filterSales/filterListings so the Price Band filter matches salePrice and
 * listPrice identically.
 */
function matchesPriceBand(price, bandIndex) {
    if (bandIndex === 'all')
        return true;
    const band = PRICE_BANDS[bandIndex];
    if (!band)
        return true;
    return price >= band.min && (band.max === null || price < band.max);
}
/**
 * '#RRGGBB' -> 'rgba(r, g, b, alpha)' - used to turn PROPERTY_TYPE_COLORS'
 * solid hues into translucent area fills for Summary's stacked area chart
 * while keeping the border itself fully opaque.
 */
export function hexToRgba(hex, alpha) {
    const normalized = hex.replace('#', '');
    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/**
 * Classifies a sale or listing into the two buckets worth distinguishing
 * (plus a catch-all) from MLS's raw property_type/property_subtype, falling
 * back to the parcel-level tmkPropertyGroup (tmk_records.property_group)
 * when those are missing. Condominium is its own property_subtype under
 * property_type='Residential' in this MLS's data (confirmed live 2026-10:
 * 428 Condominium vs 271 Single Family Residence sales, both under
 * 'Residential'), so it has to be checked before the plain House match.
 * No Land bucket - MLS's 'Land' property_type has the same roughly-6-month
 * coverage cliff as everything else MLS-sourced, and there's no
 * tmkPropertyGroup equivalent for it (see migration 056), so a dedicated
 * Land option would silently misrepresent most of the chart's own history.
 * Land sales (and Commercial Sale, Residential Income/Timeshare, and any
 * row with neither MLS nor TMK classification) all fall into 'other'.
 * Shared by the Property Type filter (filterSales/filterListings) and the
 * stacked-area breakdown (calculatePropertyTypeBreakdown).
 */
export function classifyPropertyType(item) {
    if (item.propertySubtype === 'Condominium')
        return 'condo';
    if (item.propertyType === 'Residential')
        return 'house';
    if (item.tmkPropertyGroup === 'condo')
        return 'condo';
    if (item.tmkPropertyGroup === 'house')
        return 'house';
    return 'other';
}
/**
 * House/Condo breakdown of already-filtered sales. Counts only - the
 * caller (SalesChart's buildPropertyTypeBreakdownHtml) divides by total for
 * display, since what "total" means (all filtered sales vs. only those
 * with a known type) is a presentation choice, not a data one.
 */
export function calculatePropertyTypeBreakdown(sales) {
    const breakdown = { house: 0, condo: 0, other: 0, total: sales.length };
    for (const sale of sales) {
        breakdown[classifyPropertyType(sale)]++;
    }
    return breakdown;
}
/**
 * Calculate linear regression for trend line
 * Returns slope and intercept for y = mx + b
 */
export function calculateLinearRegression(points) {
    if (points.length < 4)
        return null;
    const n = points.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    for (const point of points) {
        sumX += point.x;
        sumY += point.y;
        sumXY += point.x * point.y;
        sumXX += point.x * point.x;
    }
    const denominator = n * sumXX - sumX * sumX;
    if (denominator === 0)
        return null;
    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept };
}
/**
 * Get date cutoff for a time period
 */
export function getDateCutoff(timePeriod) {
    const now = new Date();
    const months = TIME_PERIODS[timePeriod].months;
    return new Date(now.setMonth(now.getMonth() - months));
}
/**
 * Filter sales data based on filters
 */
export function filterSales(sales, filters) {
    const cutoff = getDateCutoff(filters.timePeriod);
    return sales.filter(sale => {
        // Time filter
        const saleDate = new Date(sale.saleDate);
        if (saleDate < cutoff)
            return false;
        // Bedrooms filter
        if (filters.bedrooms === '5+') {
            if ((sale.bedrooms ?? -1) < 5)
                return false;
        }
        else if (filters.bedrooms !== 'all' && sale.bedrooms !== filters.bedrooms) {
            return false;
        }
        // Property type filter - 'other' (Commercial/unclassified) isn't
        // individually selectable, only visible as part of 'all'.
        if (filters.propertyType !== 'all' && classifyPropertyType(sale) !== filters.propertyType) {
            return false;
        }
        // Must have price
        if (!sale.salePrice || sale.salePrice <= 0)
            return false;
        // Price band filter
        if (!matchesPriceBand(sale.salePrice, filters.priceBand))
            return false;
        // For $/sqft mode, must have sqft
        if (filters.chartMode === 'pricePerSqft' && (!sale.sqft || sale.sqft <= 0)) {
            return false;
        }
        return true;
    });
}
/**
 * Filter listings data based on filters
 */
export function filterListings(listings, filters) {
    const cutoff = getDateCutoff(filters.timePeriod);
    return listings.filter(listing => {
        // Time filter
        const listDate = new Date(listing.listDate);
        if (listDate < cutoff)
            return false;
        // Bedrooms filter
        if (filters.bedrooms === '5+') {
            if ((listing.bedrooms ?? -1) < 5)
                return false;
        }
        else if (filters.bedrooms !== 'all' && listing.bedrooms !== filters.bedrooms) {
            return false;
        }
        // Property type filter - see filterSales' matching comment.
        if (filters.propertyType !== 'all' && classifyPropertyType(listing) !== filters.propertyType) {
            return false;
        }
        // Must have price
        if (!listing.listPrice || listing.listPrice <= 0)
            return false;
        // Price band filter
        if (!matchesPriceBand(listing.listPrice, filters.priceBand))
            return false;
        // For $/sqft mode, must have sqft
        if (filters.chartMode === 'pricePerSqft' && (!listing.sqft || listing.sqft <= 0)) {
            return false;
        }
        return true;
    });
}
/**
 * Bucket already-filtered sales/listings into one row per calendar
 * quarter, zero-filled from the first FULL quarter after `cutoff` through
 * the current quarter, so the bar chart reads as a continuous timeline
 * instead of silently skipping quiet quarters. Sales bucket by saleDate;
 * listings bucket by listDate and split by status, mirroring the three
 * scatter-plot series (Sold/For Sale/Pending) one-for-one so switching
 * view modes doesn't change what's being counted.
 *
 * `cutoff` is a rolling N-years-back date (e.g. "exactly 5 years before
 * today"), not a quarter boundary - so cutoff's own quarter is almost
 * always partial. Same fix as bucketByMonth: start from the first FULL
 * quarter after the cutoff rather than presenting a partial leading
 * quarter as if it were complete. The *current* quarter is intentionally
 * still included despite also being partial - a trailing
 * quarter-to-date bar is the expected/normal reading for "this quarter",
 * unlike a stray partial quarter at the far edge of the window.
 */
export function bucketByQuarter(sales, listings, cutoff) {
    const cutoffQuarterIndex = Math.floor(cutoff.getMonth() / 3);
    const cutoffIsFullQuarter = cutoff.getDate() === 1 && cutoff.getMonth() % 3 === 0;
    const startKey = cutoffIsFullQuarter
        ? cutoff.getFullYear() * 4 + cutoffQuarterIndex
        : cutoff.getFullYear() * 4 + cutoffQuarterIndex + 1;
    const now = new Date();
    const endKey = now.getFullYear() * 4 + Math.floor(now.getMonth() / 3);
    const byQuarter = new Map();
    // Sales grouped per bucket, same reason as bucketByMonth's salesByKey -
    // lets soldByType/medianSalePrice run off the full per-quarter sale list
    // after the main loop instead of maintaining running counters inline.
    const salesByKey = new Map();
    for (let key = startKey; key <= endKey; key++) {
        const year = Math.floor(key / 4);
        const quarter = (key % 4) + 1; // 1-4
        byQuarter.set(key, {
            year,
            quarter,
            soldCount: 0,
            activeCount: 0,
            pendingCount: 0,
            isPartial: key === endKey,
            soldByType: { house: 0, condo: 0, other: 0, total: 0 },
            medianSalePrice: null,
        });
        salesByKey.set(key, []);
    }
    for (const sale of sales) {
        const d = new Date(sale.saleDate);
        const key = d.getFullYear() * 4 + Math.floor(d.getMonth() / 3);
        const bucket = byQuarter.get(key);
        if (bucket) {
            bucket.soldCount++;
            salesByKey.get(key).push(sale);
        }
    }
    for (const listing of listings) {
        const d = new Date(listing.listDate);
        const key = d.getFullYear() * 4 + Math.floor(d.getMonth() / 3);
        const bucket = byQuarter.get(key);
        if (!bucket)
            continue;
        if (listing.status === 'Active')
            bucket.activeCount++;
        else if (listing.status === 'Pending')
            bucket.pendingCount++;
    }
    for (const [key, bucket] of byQuarter) {
        const quarterSales = salesByKey.get(key) ?? [];
        bucket.soldByType = calculatePropertyTypeBreakdown(quarterSales);
        bucket.medianSalePrice = median(quarterSales.map((s) => s.salePrice));
    }
    return Array.from(byQuarter.values()).sort((a, b) => (a.year * 4 + a.quarter) - (b.year * 4 + b.quarter));
}
/**
 * Month-grain equivalent of bucketByQuarter, used for Summary's 6mo/1yr
 * window (see GroupBy's doc comment on why month, not quarter, is the
 * right grain there). Same leading-partial-period fix as bucketByQuarter:
 * a rolling cutoff almost never lands on the 1st of a month, so the first
 * bucket would otherwise be a partial month silently presented as a
 * complete one. Starts from the next full calendar month after the cutoff
 * instead. The *current* month is intentionally still included despite
 * also being partial - trailing month-to-date is the expected reading,
 * same "quarter to date" precedent as bucketByQuarter's current quarter.
 */
export function bucketByMonth(sales, listings, cutoff) {
    const cutoffIsFullMonth = cutoff.getDate() === 1;
    const startKey = cutoffIsFullMonth
        ? cutoff.getFullYear() * 12 + cutoff.getMonth()
        : cutoff.getFullYear() * 12 + cutoff.getMonth() + 1;
    const now = new Date();
    const endKey = now.getFullYear() * 12 + now.getMonth();
    const byMonth = new Map();
    // Sales grouped per bucket so soldByType can run calculatePropertyTypeBreakdown
    // per month after the main loop, rather than maintaining four running
    // counters inline - this only runs for 6m/1y (the only windows that reach
    // month grain), so the extra pass is cheap.
    const salesByKey = new Map();
    for (let key = startKey; key <= endKey; key++) {
        const year = Math.floor(key / 12);
        const month = (key % 12) + 1; // 1-12
        byMonth.set(key, {
            year,
            month,
            soldCount: 0,
            activeCount: 0,
            pendingCount: 0,
            isPartial: key === endKey,
            soldByType: { house: 0, condo: 0, other: 0, total: 0 },
            medianSalePrice: null,
        });
        salesByKey.set(key, []);
    }
    for (const sale of sales) {
        const d = new Date(sale.saleDate);
        const key = d.getFullYear() * 12 + d.getMonth();
        const bucket = byMonth.get(key);
        if (bucket) {
            bucket.soldCount++;
            salesByKey.get(key).push(sale);
        }
    }
    for (const listing of listings) {
        const d = new Date(listing.listDate);
        const key = d.getFullYear() * 12 + d.getMonth();
        const bucket = byMonth.get(key);
        if (!bucket)
            continue;
        if (listing.status === 'Active')
            bucket.activeCount++;
        else if (listing.status === 'Pending')
            bucket.pendingCount++;
    }
    for (const [key, bucket] of byMonth) {
        const monthSales = salesByKey.get(key) ?? [];
        bucket.soldByType = calculatePropertyTypeBreakdown(monthSales);
        bucket.medianSalePrice = median(monthSales.map((s) => s.salePrice));
    }
    return Array.from(byMonth.values()).sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));
}
/**
 * Get unique bedroom buckets from data - 0 through 4 (and Studio) as exact
 * values, everything 5 and up folded into a single '5+' bucket so e.g. an
 * 18-bedroom estate doesn't produce a run of near-empty buttons from 5 to
 * 18. Only returns a bucket if at least one item actually has it, same as
 * before this capping was added.
 */
export function getUniqueBedrooms(data) {
    const exact = new Set();
    let hasFivePlus = false;
    for (const item of [...data.sales, ...data.listings]) {
        if (item.bedrooms === undefined || item.bedrooms < 0)
            continue;
        if (item.bedrooms >= 5)
            hasFivePlus = true;
        else
            exact.add(item.bedrooms);
    }
    const buckets = Array.from(exact).sort((a, b) => a - b);
    if (hasFivePlus)
        buckets.push('5+');
    return buckets;
}
/**
 * Plain median - used for the Summary view's Median Price metric
 * (medianSalePrice on QuarterBucket/MonthBucket). Unlike an average, a median
 * is never distorted by one outlier luxury sale in a thin month, which is
 * exactly the kind of sample size Summary buckets routinely are.
 */
function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
/**
 * Nearest-rank percentile (p in [0, 1]) - used by computeYCeiling below.
 * Not interpolated; matches the quick calibration script used to pick
 * computeYCeiling's constants against live data, so the two stay
 * comparable if those constants are ever revisited.
 */
function percentile(values, p) {
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
    return sorted[idx];
}
/**
 * The Details scatter plot's Y-axis max. A plain max() lets one luxury
 * outlier (confirmed live 2026-10: a county-wide view had a $347M sale
 * against an $800K median) compress every other point - and the trend
 * line - into the bottom sliver of the chart. The 95th percentile instead
 * tracks the bulk of the data; outliers past it render clamped to this
 * ceiling with a distinct marker (see SalesChart's buildScatterDataset)
 * rather than warping the axis for everyone else. The +20% headroom keeps
 * the 95th-percentile point itself from sitting right at the top edge.
 * Self-adapting: a tightly-clustered scope (e.g. a single small
 * neighborhood) has its 95th percentile already near its max, so this
 * produces essentially the same ceiling a plain max*1.2 would - clipping
 * only actually kicks in when there's real skew to clip.
 */
export function computeYCeiling(values) {
    if (values.length === 0)
        return 1_000_000;
    return percentile(values, 0.95) * 1.2;
}
/**
 * Calculate stats from filtered sales
 */
export function calculateStats(filteredSales) {
    if (filteredSales.length === 0) {
        return {
            salesCount: 0,
            avgSalePrice: 0,
            closeRatio: 0,
            avgDaysOnMarket: null,
        };
    }
    const salesCount = filteredSales.length;
    // Average sale price
    const totalPrice = filteredSales.reduce((sum, s) => sum + s.salePrice, 0);
    const avgSalePrice = totalPrice / salesCount;
    // Close ratio (sale price / list price)
    const salesWithListPrice = filteredSales.filter(s => s.listPrice && s.listPrice > 0);
    let closeRatio = 100;
    if (salesWithListPrice.length > 0) {
        const ratioSum = salesWithListPrice.reduce((sum, s) => sum + (s.salePrice / s.listPrice) * 100, 0);
        closeRatio = ratioSum / salesWithListPrice.length;
    }
    // Average days on market - null (N/A) when too few of the filtered sales
    // actually carry DOM data to be representative, rather than silently
    // averaging a thin slice. DOM is MLS-only and falls off a cliff beyond
    // ~3 years back, so wide TimePeriod windows (5y/10y) on scopes without a
    // lot of recent volume routinely land here - see MIN_DOM_SAMPLE/
    // MIN_DOM_COVERAGE's comment for the live numbers that set these.
    const salesWithDom = filteredSales.filter(s => s.daysOnMarket !== undefined && s.daysOnMarket >= 0);
    const domCoverage = salesWithDom.length / filteredSales.length;
    let avgDaysOnMarket = null;
    if (salesWithDom.length >= MIN_DOM_SAMPLE && domCoverage >= MIN_DOM_COVERAGE) {
        avgDaysOnMarket = Math.round(salesWithDom.reduce((sum, s) => sum + s.daysOnMarket, 0) / salesWithDom.length);
    }
    return {
        salesCount,
        avgSalePrice: Math.round(avgSalePrice),
        closeRatio: Math.round(closeRatio * 10) / 10,
        avgDaysOnMarket,
    };
}
/**
 * Get Y value for a data point based on chart mode
 */
export function getYValue(point, mode, isSale) {
    const price = isSale
        ? point.salePrice
        : point.listPrice;
    if (mode === 'pricePerSqft' && point.sqft && point.sqft > 0) {
        return Math.round(price / point.sqft);
    }
    return price;
}
/**
 * Calculate negotiation percentage (list to close difference)
 */
export function getNegotiationPercent(sale) {
    if (!sale.listPrice || sale.listPrice <= 0)
        return null;
    const diff = ((sale.salePrice - sale.listPrice) / sale.listPrice) * 100;
    return Math.round(diff * 10) / 10;
}
/**
 * Format price for display
 */
export function formatPrice(amount) {
    if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(amount % 100000 === 0 ? 1 : 2)}M`;
    }
    if (amount >= 1000) {
        return `$${Math.round(amount / 1000)}K`;
    }
    return `$${amount.toLocaleString()}`;
}
/**
 * Format date for tooltips
 */
export function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
//# sourceMappingURL=utils.js.map